import type { PFOutcome, PFPlaceRequest, Side } from "../api/types";
import { cmp, format, mul, parse } from "./money";
import { parseSeriesSlug } from "./series";

/**
 * Проба predict.fun (ADR-0040): чистые правила формы и пары окна.
 *
 * Проверка здесь — СОВЕТ, как и у формы Polymarket: закон применяет сервис
 * predictfun, у которого рынок, его шаг и потолок. Но совет обязан совпадать с
 * законом по формулировкам, иначе оператор видит «всё верно», а сервис
 * отвечает отказом.
 */

/** Ряды, для которых сервис ищет пару: активы и длительности по умолчанию. */
const PAIRED_ASSETS = new Set(["BTC", "ETH", "SOL"]);
const PAIRED_DURATIONS = new Set(["5m", "15m"]);

/**
 * Есть ли у окна Polymarket пара на predict.fun. Slug у них общий
 * (`<asset>-updown-<dur>-<start>`), поэтому пара — это сам slug окна.
 */
export function pairedSlug(slug: string | undefined): string | null {
  if (!slug) return null;
  const parts = parseSeriesSlug(slug);
  if (!parts || !PAIRED_ASSETS.has(parts.asset) || !PAIRED_DURATIONS.has(parts.duration)) return null;
  return slug.trim().toLowerCase();
}

/** Минимальный номинал ордера площадки. */
export const PF_MIN_NOTIONAL = "1";

export interface PFDraft {
  outcome: "Up" | "Down";
  side: Side;
  price: string;
  size: string;
  postOnly: boolean;
}

export interface PFCheck {
  problems: { price?: string; size?: string };
  /** Номинал в USDT, если цена и количество разобраны. */
  notional: string | null;
  request: PFPlaceRequest | null;
}

/**
 * Проверка черновика. tick — шаг цены рынка ("0.01"), maxOrder — потолок
 * сервиса PF_MAX_ORDER_USDT.
 */
export function checkPFDraft(slug: string, d: PFDraft, tick: string, maxOrder: string | undefined): PFCheck {
  const problems: PFCheck["problems"] = {};
  const price = parse(d.price);
  const size = parse(d.size);
  const step = parse(tick);

  if (d.price.trim() === "") problems.price = "укажите цену";
  else if (price === null) problems.price = "цена — десятичное число";
  else if (price <= 0 || price >= 1_000_000) problems.price = "цена — строго между 0 и 1";
  else if (step && price % step !== 0) problems.price = `цена не кратна шагу ${tick}`;

  if (d.size.trim() === "") problems.size = "укажите количество долей";
  else if (size === null || size <= 0) problems.size = "количество должно быть больше нуля";
  else if (size < 10_000) problems.size = "количество меньше 0.01 доли";

  let notional: string | null = null;
  if (price !== null && size !== null && !problems.price && !problems.size) {
    const n = mul(price, size);
    if (n === null) {
      problems.size = "номинал вне диапазона";
    } else {
      notional = format(n);
      const min = parse(PF_MIN_NOTIONAL) ?? 0;
      const max = parse(maxOrder ?? "");
      if (cmp(n, min) < 0) problems.size = `номинал ${notional} USDT меньше минимума площадки 1 USDT`;
      else if (max !== null && cmp(n, max) > 0)
        problems.size = `номинал ${notional} USDT больше потолка PF_MAX_ORDER_USDT=${maxOrder}`;
    }
  }

  const ok = !problems.price && !problems.size && price !== null && size !== null;
  return {
    problems,
    notional,
    request: ok
      ? {
          slug,
          outcome: d.outcome,
          side: d.side,
          price: format(price),
          size: format(size),
          post_only: d.postOnly,
        }
      : null,
  };
}

/**
 * Кнопка строки PF в быстрых действиях: M — мейкер (post-only) по своей
 * стороне стакана, T — тейкер по встречной.
 *
 *   - покупка M — по лучшей заявке, T — по лучшему предложению;
 *   - продажа M — по лучшему предложению, T — по лучшей заявке.
 *
 * Покупка задаётся суммой в USD (пресет панели), количество — вниз до сотых
 * доли: больше суммы не тратим. Продажа — долями позиции, вниз до 4 знаков:
 * площадка (SDK) режет количество до пяти значащих цифр, и продать больше,
 * чем держим, она не даст.
 */
export interface PFQuickAction {
  side: Side;
  mode: "join" | "cross";
}

export type PFQuick =
  | {
      ok: true;
      price: string;
      size: string;
      notional: string;
      postOnly: boolean;
    }
  | { ok: false; reason: string };

interface Level {
  price: string;
  size: string;
}

export function pfQuick(
  a: PFQuickAction,
  // Стороны — null-терпимые: пустая сторона стакана приходила null и роняла
  // страницу (прод 2026-10-12); сервер теперь шлёт [], но кнопка не должна
  // зависеть от этого обещания.
  book: { bids: Level[] | null; asks: Level[] | null } | null | undefined,
  input: { usd?: string | null; shares?: string | null },
  maxOrder: string | undefined,
): PFQuick {
  if (!book) return { ok: false, reason: "нет стакана predict.fun" };
  const bid = book.bids?.[0]?.price;
  const ask = book.asks?.[0]?.price;
  const ownSide = a.side === "BUY" ? bid : ask;
  const otherSide = a.side === "BUY" ? ask : bid;
  const priceStr = a.mode === "join" ? ownSide : otherSide;
  if (!priceStr) {
    return {
      ok: false,
      reason:
        (a.side === "BUY") === (a.mode === "join") ? "нет заявок в стакане" : "нет предложений в стакане",
    };
  }
  const price = parse(priceStr);
  if (price === null || price <= 0) return { ok: false, reason: "цена стакана не разобрана" };

  let size: number;
  if (a.side === "BUY") {
    const usd = parse(input.usd ?? null);
    if (usd === null || usd <= 0) return { ok: false, reason: "не выбрана сумма" };
    // доли = usd / price, вниз до сотых: 1_000_000 микро = 1 доля.
    size = Math.floor((usd * 100) / price) * 10_000;
  } else {
    const shares = parse(input.shares ?? null);
    if (shares === null || shares <= 0) return { ok: false, reason: "закрывать нечего" };
    size = Math.floor(shares / 100) * 100;
  }
  if (size <= 0) return { ok: false, reason: "на эту сумму не выходит и сотой доли" };
  const n = mul(price, size);
  if (n === null) return { ok: false, reason: "номинал вне диапазона" };
  const notional = format(n);
  const min = parse(PF_MIN_NOTIONAL) ?? 0;
  if (cmp(n, min) < 0)
    return {
      ok: false,
      reason: `номинал ${notional} USDT меньше минимума площадки 1 USDT`,
    };
  const max = parse(maxOrder ?? "");
  if (max !== null && cmp(n, max) > 0) {
    return {
      ok: false,
      reason: `номинал ${notional} USDT больше потолка PF_MAX_ORDER_USDT=${maxOrder}`,
    };
  }
  return {
    ok: true,
    price: format(price),
    size: format(size),
    notional,
    postOnly: a.mode === "join",
  };
}

/**
 * Итог ордера predict.fun словами и тоном (ADR-0041). Успех — только «стоит»
 * и «исполнен»: неподтверждённый ордер — предупреждение, а не успех, потому
 * что 201 площадки ещё не значит «в стакане».
 */
export function pfOutcomeText(o: PFOutcome): { text: string; tone: "ok" | "bad" | "muted" } {
  const why = o.reason ? `: ${o.reason}` : "";
  switch (o.status) {
    case "OPEN":
      return { text: `стоит в стакане (${o.order_id})`, tone: "ok" };
    case "FILLED":
      return { text: `исполнен ${o.filled} долей (${o.order_id})${o.reason ? ` — ${o.reason}` : ""}`, tone: "ok" };
    case "PENDING":
      return { text: `пересекает стакан, площадка ещё не свела (${o.order_id})`, tone: "bad" };
    case "REJECTED":
      return { text: `не поставлен${why} (${o.order_id})`, tone: "bad" };
    case "UNCONFIRMED":
      return { text: `принят, статус не подтверждён${why} (${o.order_id}) — проверьте список ордеров`, tone: "bad" };
    default:
      return { text: `принят, итога пока нет (${o.order_id}) — проверьте список ордеров`, tone: "muted" };
  }
}
